import { Box, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';
import { LoadingSpinner } from '@verantos-dev/shared-components';
import { PeopleSection } from '@verantos-dev/ui-annotation-tool';

import { useReviewAgentSummary } from './useReviewAgentSummary';
import { useReviewAndCreate } from './useReviewAndCreate';
import { AgentAllocationSummaryTable } from '../ReviewAndCreate/AgentAllocationSummaryTable';
import { AllocationSummaryTable } from '../ReviewAndCreate/AllocationSummaryTable';
import { AnnotationAgentSummary } from '../ReviewAndCreate/AnnotationAgentSummary';
import { DatasetSummary } from '../ReviewAndCreate/DatasetSummary';
import { ProjectBasics } from '../ReviewAndCreate/ProjectBasics';
import { ReviewConceptAndAttributes } from '../ReviewAndCreate/ReviewConceptAndAttributes';

interface ReviewAndCreateProps {
  setRequiredFieldsFilled: (fieldsFilled: boolean) => void;
}

export const ReviewAndCreate = ({ setRequiredFieldsFilled }: ReviewAndCreateProps) => {
  const theme = useTheme();
  const {
    projectName,
    conditionAreaName,
    healthSystemName,
    annotatorNames,
    supervisorNames,
    addedConcepts,
    validationEncounterCount,
    testEncounterCount,
    trainingEncounterCount,
    allocationSummary,
    isAllocationSummaryLoading,
    isDataSplitSummaryLoading,
    agentAllocation,
  } = useReviewAndCreate();
  // TODO: cleanup with cp_annotation_createproject_agent ff
  const agentSummary = useReviewAgentSummary();
  // TODO: cleanup with cp_annotation_show_dashboard_loading_spinner ff
  const showLoadingSpinner = useFeatureFlagSplit(
    'cp_annotation_show_dashboard_loading_spinner'
  ) as boolean;
  const isLoading = isAllocationSummaryLoading || isDataSplitSummaryLoading;
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ p: theme.spacing(4) }}>
        <ProjectBasics
          projectName={projectName}
          conditionAreaName={conditionAreaName}
          healthSystemName={healthSystemName}
        />
      </Box>
      {/* TODO: cleanup with cp_annotation_createproject_agent ff */}
      {agentSummary && (
        <Box sx={{ p: theme.spacing(4) }}>
          <AnnotationAgentSummary summary={agentSummary} />
        </Box>
      )}
      <Box sx={{ p: theme.spacing(4) }}>
        <PeopleSection annotatorNames={annotatorNames} supervisorNames={supervisorNames} />
      </Box>

      {showLoadingSpinner && isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <LoadingSpinner id="reviewAndCreateLoading" />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: theme.spacing(2), p: theme.spacing(4) }}>
          <Box sx={{ flexGrow: 1 }}>
            <ReviewConceptAndAttributes addedConcepts={addedConcepts} />
          </Box>

          <Box
            id="review-sidebar"
            sx={{
              width: '450px',
              p: theme.spacing(4),
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(4),
            }}
          >
            <DatasetSummary
              validationEncounterCount={validationEncounterCount}
              testEncounterCount={testEncounterCount}
              trainingEncounterCount={trainingEncounterCount}
            />
            {/* TODO: cleanup with cp_annotation_createproject_agent ff */}
            {agentAllocation ? (
              <AgentAllocationSummaryTable
                status={agentAllocation.status}
                preview={agentAllocation.preview}
                annotators={agentAllocation.annotators}
                isCustom={agentAllocation.isCustom}
              />
            ) : (
              <AllocationSummaryTable allocationSummary={allocationSummary} />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
